import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Clapperboard, Podcast, Film, Video, TrendingUp, Rocket, Users, Share2, Globe,
  Bot, Workflow, Code2, BrainCircuit, ArrowRight, MessageCircle, Menu, X,
} from "lucide-react";
import { useState } from "react";
import { WHATSAPP_URL } from "@/lib/api";

const units = [
  {
    id: "studios",
    name: "NX Studios",
    tag: "Produção Audiovisual",
    desc: "Podcasts, documentários, captação e edição de vídeos com padrão cinematográfico profissional.",
    img: "https://images.unsplash.com/photo-1589903308904-1010c2294adc?crop=entropy&cs=srgb&fm=jpg&q=85",
    items: [
      { icon: Podcast, label: "Podcasts" },
      { icon: Film, label: "Documentários" },
      { icon: Video, label: "Captação" },
      { icon: Clapperboard, label: "Edição" },
    ],
  },
  {
    id: "marketing",
    name: "NX Marketing Digital",
    tag: "Growth & Performance",
    desc: "Tráfego pago, lançamentos, mentorias, social media e sites de alta conversão.",
    img: "https://images.unsplash.com/photo-1758873271824-a3216c80d1ac?crop=entropy&cs=srgb&fm=jpg&q=85",
    items: [
      { icon: TrendingUp, label: "Tráfego Pago" },
      { icon: Rocket, label: "Lançamentos" },
      { icon: Users, label: "Mentorias" },
      { icon: Share2, label: "Social Media" },
      { icon: Globe, label: "Sites" },
    ],
  },
  {
    id: "iatech",
    name: "NX IA-Tech",
    tag: "Tecnologia & IA",
    desc: "Sistemas web, agentes de IA, automação de processos e produtos tecnológicos.",
    img: "https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&q=85",
    items: [
      { icon: Code2, label: "Sistemas Web" },
      { icon: Bot, label: "Agentes de IA" },
      { icon: Workflow, label: "Automação" },
      { icon: BrainCircuit, label: "Gestão & Vendas" },
    ],
  },
];

const tickerItems = ["NX STUDIOS", "NX MARKETING DIGITAL", "NX IA-TECH", "AUDIOVISUAL", "TRÁFEGO PAGO", "AGENTES DE IA", "AUTOMAÇÃO", "LANÇAMENTOS"];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, delay },
});

export default function Landing() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-nx-dark text-white font-jakarta overflow-x-hidden">
      {/* NAV */}
      <header className="fixed top-0 inset-x-0 z-50 glass" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <img src="/logo-mark.png" alt="NX" className="h-8 w-auto" />
            <span className="font-mono text-[11px] tracking-[0.35em] text-white">COMPANY</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <a href="#unidades" className="hover:text-nx-yellow transition-colors" data-testid="nav-landing-units">Unidades</a>
            <a href="#servicos" className="hover:text-nx-yellow transition-colors" data-testid="nav-landing-services">Serviços</a>
            <a href="#plataforma" className="hover:text-nx-yellow transition-colors" data-testid="nav-landing-platform">Plataforma</a>
            <a href="#sobre" className="hover:text-nx-yellow transition-colors" data-testid="nav-landing-about">Sobre</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link
              to="/login"
              data-testid="nav-landing-login-btn"
              className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold border border-zinc-700 hover:border-nx-yellow hover:text-nx-yellow transition-colors px-4 py-2 rounded-full"
            >
              Área do Cliente
            </Link>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-contact-button"
              className="inline-flex items-center gap-2 bg-nx-yellow text-nx-dark text-sm font-bold px-4 py-2 rounded-full hover:bg-nx-electric transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              Falar no WhatsApp
            </a>
            <button className="md:hidden text-white" onClick={() => setMenuOpen(!menuOpen)} data-testid="nav-mobile-toggle">
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden glass border-t border-zinc-800 px-6 py-4 flex flex-col gap-4 text-sm" data-testid="nav-mobile-menu">
            <a href="#unidades" onClick={() => setMenuOpen(false)} className="text-zinc-300">Unidades</a>
            <a href="#servicos" onClick={() => setMenuOpen(false)} className="text-zinc-300">Serviços</a>
            <a href="#plataforma" onClick={() => setMenuOpen(false)} className="text-zinc-300">Plataforma</a>
            <a href="#sobre" onClick={() => setMenuOpen(false)} className="text-zinc-300">Sobre</a>
            <Link to="/login" className="text-nx-yellow font-semibold">Área do Cliente</Link>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="relative min-h-screen flex items-center bg-grid pt-16" data-testid="hero-section">
        <div className="absolute top-1/4 -right-40 w-[500px] h-[500px] rounded-full bg-nx-yellow/10 blur-[120px] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center w-full min-w-0">
          <div className="lg:col-span-7 min-w-0 max-w-full">
            <motion.p {...fade(0)} className="font-mono text-xs uppercase tracking-[0.15em] sm:tracking-[0.3em] text-nx-yellow mb-6" data-testid="hero-eyebrow">
              Ecossistema Empresarial Integrado
            </motion.p>
            <motion.h1 {...fade(0.1)} className="font-syne text-[2rem] sm:text-5xl font-extrabold leading-[1.05] tracking-tight break-words" data-testid="hero-title">
              Criamos.<br />
              Escalamos.<br />
              <span className="text-nx-yellow">Automatizamos.</span>
            </motion.h1>
            <motion.p {...fade(0.2)} className="mt-6 text-zinc-400 text-base sm:text-lg max-w-xl leading-relaxed" data-testid="hero-subtitle">
              A NX COMPANY une produção audiovisual, marketing de performance e inteligência artificial
              em um único ecossistema para acelerar o crescimento do seu negócio.
            </motion.p>
            <motion.div {...fade(0.3)} className="mt-10 flex flex-wrap gap-4">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-cta"
                className="inline-flex items-center gap-2 bg-nx-yellow text-nx-dark font-bold px-7 py-3.5 rounded-full hover:bg-nx-electric transition-colors yellow-glow"
              >
                <MessageCircle className="w-5 h-5" />
                Iniciar conversa
              </a>
              <a
                href="#unidades"
                data-testid="hero-units-cta"
                className="inline-flex items-center gap-2 border border-zinc-700 hover:border-nx-yellow hover:text-nx-yellow transition-colors font-semibold px-7 py-3.5 rounded-full"
              >
                Conhecer as unidades
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
            <motion.div {...fade(0.4)} className="mt-14 grid grid-cols-3 gap-6 max-w-md" data-testid="hero-stats">
              {[
                { n: "03", l: "Unidades de negócio" },
                { n: "+20", l: "Serviços integrados" },
                { n: "01", l: "Ecossistema completo" },
              ].map((s) => (
                <div key={s.l}>
                  <p className="font-mono text-2xl sm:text-3xl font-bold text-nx-yellow">{s.n}</p>
                  <p className="text-xs text-zinc-500 mt-1 leading-snug">{s.l}</p>
                </div>
              ))}
            </motion.div>
          </div>
          <motion.div {...fade(0.25)} className="lg:col-span-5 relative hidden lg:block min-w-0 max-w-full">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1559523161-0fc0d8b38a7a?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Estúdio profissional NX"
                className="rounded-3xl border border-zinc-800 object-cover h-[520px] w-full"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-6 -left-6 glass rounded-2xl p-5 yellow-glow" data-testid="hero-metric-card">
                <p className="font-mono text-xs text-zinc-400 uppercase tracking-widest">Em operação</p>
                <p className="font-syne text-xl font-bold text-white mt-1">Studios + Marketing + IA</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TICKER */}
      <div className="border-y border-zinc-800 bg-nx-surface overflow-hidden py-4" data-testid="brand-ticker">
        <div className="ticker-track flex whitespace-nowrap gap-10 w-max">
          {[...tickerItems, ...tickerItems].map((t, i) => (
            <span key={i} className="font-syne font-bold text-sm tracking-[0.25em] text-zinc-600 flex items-center gap-10">
              {t} <span className="text-nx-yellow">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* UNITS */}
      <section id="unidades" className="max-w-7xl mx-auto px-6 py-28" data-testid="units-section">
        <motion.div {...fade(0)} className="max-w-2xl">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-nx-yellow mb-4">Unidades estratégicas</p>
          <h2 className="font-syne text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" data-testid="units-title">
            Três frentes. <span className="text-zinc-500">Um só padrão de excelência.</span>
          </h2>
        </motion.div>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {units.map((u, i) => (
            <motion.article
              key={u.id}
              {...fade(0.1 * i)}
              className="nx-card card-hover overflow-hidden group"
              data-testid={`unit-card-${u.id}`}
            >
              <div className="relative h-52 overflow-hidden">
                <img src={u.img} alt={u.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121216] via-transparent to-transparent" />
                <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] bg-nx-yellow text-nx-dark font-bold px-3 py-1 rounded-full">
                  {u.tag}
                </span>
              </div>
              <div className="p-7">
                <h3 className="font-syne text-2xl font-bold">{u.name}</h3>
                <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{u.desc}</p>
                <ul className="mt-5 flex flex-wrap gap-2">
                  {u.items.map((it) => (
                    <li key={it.label} className="inline-flex items-center gap-1.5 text-xs text-zinc-300 bg-zinc-800/60 border border-zinc-700/60 px-3 py-1.5 rounded-full">
                      <it.icon className="w-3.5 h-3.5 text-nx-yellow" />
                      {it.label}
                    </li>
                  ))}
                </ul>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`unit-cta-${u.id}`}
                  className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-nx-yellow hover:gap-3 transition-all"
                >
                  Solicitar proposta <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* SERVICES STRIP */}
      <section id="servicos" className="border-y border-zinc-800 bg-nx-surface" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fade(0)}>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-nx-yellow mb-4">Serviços</p>
            <h2 className="font-syne text-3xl sm:text-4xl font-extrabold tracking-tight">
              Da captação à automação, tudo sob o mesmo teto.
            </h2>
            <p className="text-zinc-400 mt-4 leading-relaxed">
              Elimine a fricção de contratar várias agências. A NX COMPANY entrega estratégia,
              produção e tecnologia com gestão centralizada.
            </p>
          </motion.div>
          <motion.div {...fade(0.15)} className="grid grid-cols-2 gap-3">
            {[
              "Produção audiovisual", "Podcasts & documentários", "Tráfego pago", "Lançamentos digitais",
              "Mentorias de negócio", "Social media", "Sites de conversão", "Sistemas web sob medida",
              "Agentes de IA", "Automação de processos", "Sistemas de gestão", "Produtos tecnológicos",
            ].map((s) => (
              <div key={s} className="flex items-center gap-3 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-zinc-300 hover:border-nx-yellow/40 hover:text-white transition-colors">
                <span className="w-1.5 h-1.5 rounded-full bg-nx-yellow shrink-0" />
                {s}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* PLATFORM TEASER */}
      <section id="plataforma" className="max-w-7xl mx-auto px-6 py-28" data-testid="platform-section">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <motion.div {...fade(0)} className="relative order-2 lg:order-1">
            <div className="nx-card p-6 yellow-glow">
              <div className="flex items-center gap-2 mb-5">
                <span className="w-3 h-3 rounded-full bg-zinc-700" />
                <span className="w-3 h-3 rounded-full bg-zinc-700" />
                <span className="w-3 h-3 rounded-full bg-nx-yellow" />
                <span className="ml-3 font-mono text-[10px] text-zinc-500 tracking-widest uppercase">NX OS · Gestão integrada</span>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-3">
                {[
                  { l: "Fluxo de Caixa", v: "R$ 24,9k" },
                  { l: "A Receber", v: "R$ 11,1k" },
                  { l: "Leads Ativos", v: "08" },
                ].map((k) => (
                  <div key={k.l} className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{k.l}</p>
                    <p className="font-mono text-lg font-bold text-nx-yellow mt-1">{k.v}</p>
                  </div>
                ))}
              </div>
              <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4 space-y-2.5">
                {[
                  { n: "Lima Fitness", s: "Proposta", w: "w-2/3" },
                  { n: "CR Imóveis", s: "Negociação", w: "w-4/5" },
                  { n: "JP Advocacia", s: "Qualificação", w: "w-1/3" },
                ].map((d) => (
                  <div key={d.n} className="flex items-center gap-3">
                    <span className="text-xs text-zinc-300 w-28 truncate">{d.n}</span>
                    <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                      <div className={`h-full bg-nx-yellow rounded-full ${d.w}`} />
                    </div>
                    <span className="text-[10px] font-mono text-zinc-500">{d.s}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
          <motion.div {...fade(0.1)} className="order-1 lg:order-2">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-nx-yellow mb-4">NX OS · ERP + CRM</p>
            <h2 className="font-syne text-3xl sm:text-4xl font-extrabold tracking-tight" data-testid="platform-title">
              Gestão financeira e comercial em uma única plataforma.
            </h2>
            <p className="text-zinc-400 mt-4 leading-relaxed">
              Pipeline comercial em kanban, base de clientes, contas a pagar e a receber e fluxo de caixa
              em tempo real — o mesmo sistema que usamos para gerir a NX, disponível para nossos clientes.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-zinc-300">
              {["Pipeline de vendas visual por etapas", "Contas a pagar e a receber com alertas", "Fluxo de caixa mensal consolidado", "Visão por unidade de negócio"].map((f) => (
                <li key={f} className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded-full bg-nx-yellow/15 border border-nx-yellow/40 flex items-center justify-center shrink-0">
                    <span className="w-1.5 h-1.5 rounded-full bg-nx-yellow" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
            <Link
              to="/login"
              data-testid="platform-login-cta"
              className="mt-8 inline-flex items-center gap-2 bg-nx-yellow text-nx-dark font-bold px-7 py-3.5 rounded-full hover:bg-nx-electric transition-colors"
            >
              Acessar a plataforma <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="sobre" className="border-t border-zinc-800 bg-nx-surface" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-10">
          <motion.p {...fade(0)} className="lg:col-span-4 font-mono text-xs uppercase tracking-[0.3em] text-nx-yellow">
            Sobre a NX
          </motion.p>
          <motion.div {...fade(0.1)} className="lg:col-span-8">
            <h2 className="font-syne text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
              Uma holding criativa e tecnológica construída para a nova economia digital brasileira.
            </h2>
            <p className="text-zinc-400 mt-6 max-w-2xl leading-relaxed">
              Nascemos da convergência entre comunicação, performance e tecnologia. Cada unidade opera
              com autonomia técnica, mas compartilha a mesma obsessão: resultado mensurável para o cliente.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-zinc-800" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="flex items-center gap-3" data-testid="footer-logo">
              <img src="/logo-mark.png" alt="NX" className="h-10 w-auto" />
              <span className="font-mono text-xs tracking-[0.35em] text-white">COMPANY</span>
            </div>
            <p className="text-xs text-zinc-500 mt-2 font-mono tracking-widest uppercase">Studios · Marketing Digital · IA-Tech</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-cta"
              className="inline-flex items-center gap-2 bg-nx-yellow text-nx-dark font-bold px-6 py-3 rounded-full hover:bg-nx-electric transition-colors text-sm"
            >
              <MessageCircle className="w-4 h-4" /> Falar com a NX
            </a>
            <Link
              to="/login"
              data-testid="footer-login-link"
              className="inline-flex items-center gap-2 border border-zinc-700 hover:border-nx-yellow hover:text-nx-yellow transition-colors font-semibold px-6 py-3 rounded-full text-sm"
            >
              Área do Cliente
            </Link>
          </div>
        </div>
        <div className="border-t border-zinc-900 py-5 text-center text-[11px] text-zinc-600 font-mono tracking-widest">
          © 2026 NX COMPANY — TODOS OS DIREITOS RESERVADOS
        </div>
      </footer>

      {/* FLOATING WHATSAPP */}
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="whatsapp-floating-trigger"
        aria-label="Falar no WhatsApp"
        className="pulse-ring fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25d366] flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
      >
        <MessageCircle className="w-7 h-7 text-white" fill="white" />
      </a>
    </div>
  );
}
